import "bootstrap/dist/css/bootstrap.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./index.css";

import { Redirect, Route, Router, Switch } from "react-router-dom";
import { applyMiddleware, compose, createStore } from "redux";
import { hasAdminAccess, hasBackofficeAccess } from "./services/roles";
import { useEffect, useState } from "react";

import Administration from "./administration";
import AdministrationCompany from "./administration/companies/company";
import AdministrationContract from "./administration/contract";
import AdministrationContractUsers from "./administration/contract/ContractUsers";
import { AlertToasts } from "./components/AlertToasts";
import { AnalyticsLayout } from "./analyticsDashboard/AnalyticsLayout";
import Applications from "./applications";
import AskPasswordReset from "./password/askPasswordReset";
import CampaignPage from "./clientDashboard/CampaignPage";
import CampaignsPage from "./clientDashboard";
import ClientContact from "./contact";
import ErrorBoundary from "./components/errorBoundary";
import { Glossary } from "./glossary/Glossary";
import Landing from "./landing";
import { Layout } from "./Layout";
import { Legals } from "./legals";
import { MissionMonitoringDashboard } from "./missionMonitoringDashboard";
import { Page404 } from "./components/page404";
import PasswordEdition from "./password/edit";
import PendingAccess from "./components/pendingAccess";
import { Provider } from "react-redux";
import RefreshUser from "./components/refreshUser";
import SitePage from "./clientDashboard/SitePage";
import { SnackbarProvider } from "notistack";
import Support from "./support";
import { Tooltip } from "react-tooltip";
import { TrackingProvider } from "./analytics";
import Upload from "./upload";
import UploadSession from "./upload/session";
import UploadSessions from "./uploadsessions";
import UserPage from "./users/UserPage";
import Users from "./users";
import { WorldMapLayout } from "./WorldMap";
import _ from "lodash";
import { breadcrumbMiddleware } from "./services/breadcrumbs";
import { connectedRouterRedirect } from "redux-auth-wrapper/history4/redirect";
import { createBrowserHistory } from "history";
import createRootReducer from "./reducers";
import missionMonitoringResults from "./missionMonitoringResults/missionMonitoringResults";
import { persistStore } from "redux-persist";
import thunk from "redux-thunk";

const history = createBrowserHistory();

/* DEV: Display store for redux pluggin */
/* const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__; */
const composeEnhancers = compose;
const store = createStore(
  createRootReducer(),
  composeEnhancers(applyMiddleware(thunk, breadcrumbMiddleware))
);

// Route changes are not Redux actions anymore: dispatch a no-op one so the
// crash report breadcrumbs keep recording navigations. No reducer handles it.
history.listen(() => store.dispatch({ type: "@@router/LOCATION_CHANGE" }));

const userIsAuthenticated = connectedRouterRedirect({
  redirectPath: "/",
  authenticatedSelector: (state) => {
    return !_.isEmpty(state.user.self);
  },
  wrapperDisplayName: "UserIsAuthenticated",
});

const userIsNotAuthenticated = connectedRouterRedirect({
  redirectPath: (state, query) => {
    const location = query.location;

    let params = new URLSearchParams(location.search);
    const redirect = params.get("redirect");

    if (redirect) return redirect;
    return hasBackofficeAccess(state.user.self) ? "/administration" : "/map";
  },
  allowRedirectBack: false,
  authenticatedSelector: (state) => {
    return _.isEmpty(state.user.self);
  },
  wrapperDisplayName: "UserIsNotAuthenticated",
  redirectQueryParamName: false,
});

const userHasAccess = connectedRouterRedirect({
  redirectPath: "/pendingaccess",
  authenticatedSelector: (state) => {
    return !_.isEmpty(state.user.self) && state.user.self.access;
  },
  wrapperDisplayName: "UserHasAccess",
});

const userIsBackoffice = connectedRouterRedirect({
  redirectPath: "/pendingaccess",
  authenticatedSelector: (state) => {
    return !_.isEmpty(state.user.self) && hasBackofficeAccess(state.user.self);
  },
  wrapperDisplayName: "UserIsBackoffice",
});

const userIsAdmin = connectedRouterRedirect({
  redirectPath: "/pendingaccess",
  authenticatedSelector: (state) => {
    return !_.isEmpty(state.user.self) && hasAdminAccess(state.user.self);
  },
  wrapperDisplayName: "UserIsAdmin",
});

// Common pages
const LandingPage = userIsNotAuthenticated(Landing);
const AskPasswordResetPage = userIsNotAuthenticated(AskPasswordReset);
const PendingPage = userIsAuthenticated(PendingAccess);
const UsersPage = userIsAuthenticated(userIsBackoffice(Users));
const UserDetailPage = userIsAuthenticated(userIsBackoffice(UserPage));
const UploadSessionsPage = userIsAuthenticated(
  userIsBackoffice(UploadSessions)
);

// BackOffice Administration Pages
const AdministrationPage = userIsAuthenticated(
  userIsBackoffice(Administration)
);
const AdministrationCompanyPage = userIsAuthenticated(
  userIsBackoffice(AdministrationCompany)
);
const AdministrationContractPage = userIsAuthenticated(
  userIsBackoffice(AdministrationContract)
);
const AdministrationContractUsersPage = userIsAuthenticated(
  userIsBackoffice(AdministrationContractUsers)
);
const SupportPage = userIsAuthenticated(userIsAdmin(Support));
const ApplicationsPage = userIsAuthenticated(userIsBackoffice(Applications));
const WorldMapLayoutPage = userIsAuthenticated(userHasAccess(WorldMapLayout));
const ClientContactPage = userIsAuthenticated(userHasAccess(ClientContact));
const CampaignsListPage = userIsAuthenticated(userHasAccess(CampaignsPage));
const CampaignDetailPage = userIsAuthenticated(userHasAccess(CampaignPage));
const SiteDetailPage = userIsAuthenticated(userHasAccess(SitePage));
const GlossaryPage = userIsAuthenticated(userHasAccess(Glossary));
const UploadPage = userIsAuthenticated(userHasAccess(Upload));
const UploadSessionPage = userIsAuthenticated(userHasAccess(UploadSession));
const MissionMonitoringDashboardPage = userIsAuthenticated(
  userHasAccess(MissionMonitoringDashboard)
);

const MissionMonitoringResultPage = userIsAuthenticated(
  userHasAccess(missionMonitoringResults)
);
const AnalyticsPage = userIsAuthenticated(userHasAccess(AnalyticsLayout));

// eslint-disable-next-line max-lines-per-function
export default function App() {
  const [rehydrated, setRehydrated] = useState(false);

  useEffect(() => {
    persistStore(store, null, () => setRehydrated(true));
  });

  return !rehydrated ? (
    <div>Loading...</div>
  ) : (
    <Provider store={store}>
      <ErrorBoundary>
        <SnackbarProvider
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <AlertToasts />
          <RefreshUser>
            <TrackingProvider>
              <Router history={history}>
                <Switch>
                  <Route component={LandingPage} exact path="/" />
                  <Redirect exact from="/clientDashboard" to="/campaigns" />
                  <Route
                    component={AskPasswordResetPage}
                    path="/password/askPasswordReset"
                  />
                  {/* Not wrapped in userIsNotAuthenticated: a reset link must
                      work even when a session is already open in the browser. */}
                  <Route component={PasswordEdition} path="/password/edit" />
                  <Layout>
                    {/* Layout has no `path`, so the outer Switch always stops
                        here: the 404 has to be the fallback of an inner Switch
                        to ever render. */}
                    <Switch>
                      <Route component={UploadPage} exact path="/upload" />
                      <Route
                        component={UploadSessionPage}
                        exact
                        path="/upload/:sessionId"
                      />
                      <Route component={UsersPage} exact path="/users" />
                      <Route
                        exact
                        path="/users/:id"
                        // Keyed by id: going from one user to another (the navbar role
                        // badge leads to one's own page) must remount, or the access
                        // draft of the previous user would be saved onto the next one.
                        render={(props) => (
                          <UserDetailPage
                            key={props.match.params.id}
                            {...props}
                          />
                        )}
                      />
                      <Route
                        component={UploadSessionsPage}
                        exact
                        path="/uploadsessions"
                      />
                      <Route
                        component={AdministrationPage}
                        exact
                        path="/administration"
                      />
                      <Route
                        component={AdministrationCompanyPage}
                        exact
                        path="/administration/company/:companyId"
                      />
                      <Route
                        component={AdministrationContractPage}
                        exact
                        path="/administration/company/:companyId/contract/:contractId"
                      />
                      <Route
                        component={AdministrationContractUsersPage}
                        exact
                        path="/administration/company/:companyId/contract/:contractId/users"
                      />
                      <Route component={SupportPage} exact path="/support" />
                      <Route
                        component={ApplicationsPage}
                        exact
                        path="/applications"
                      />
                      <Route
                        component={WorldMapLayoutPage}
                        path="/map/:trialId?/:date?"
                      />
                      <Route
                        component={CampaignsListPage}
                        exact
                        path="/campaigns"
                      />
                      {/* Keyed on the ids: the global search jumps straight
                          from one campaign (or site) to another on the same
                          route, which would otherwise keep the page mounted
                          and carry its filters over to the new one. */}
                      <Route
                        exact
                        path="/campaigns/:campaignId"
                        render={(props) => (
                          <CampaignDetailPage
                            key={props.match.params.campaignId}
                            {...props}
                          />
                        )}
                      />
                      <Route
                        exact
                        path="/campaigns/:campaignId/sites/:siteId"
                        render={(props) => (
                          <SiteDetailPage
                            key={props.match.params.siteId}
                            {...props}
                          />
                        )}
                      />
                      <Route
                        component={ClientContactPage}
                        exact
                        path="/contact"
                      />
                      <Route component={GlossaryPage} exact path="/glossary" />
                      <Route component={AnalyticsPage} path="/analytics" />
                      <Route
                        component={PendingPage}
                        exact
                        path="/pendingaccess"
                      />
                      <Route
                        component={MissionMonitoringDashboardPage}
                        exact
                        path="/monitoring"
                      />
                      <Route
                        component={MissionMonitoringResultPage}
                        exact
                        path="/monitoring/:mmId"
                      />
                      <Route component={Legals} exact path="/legals" />
                      <Route component={Page404} />
                    </Switch>
                  </Layout>
                </Switch>
              </Router>
            </TrackingProvider>
          </RefreshUser>
        </SnackbarProvider>
        <Tooltip
          id="tooltip"
          className="tooltip-custom"
          place="bottom"
          variant="success"
        />
      </ErrorBoundary>
    </Provider>
  );
}
